import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import "react-toastify/dist/ReactToastify.css";
import { GoogleLogin } from '../Google/LoginByGG';
import { DatePicker, Flex, GetProps, Input, message } from 'antd';

import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import Spinner from '@/components/Spinner';
import { Helmet } from 'react-helmet-async';
import { API_USER_URL } from '@/env';
import { checkRefCodeExists, loginUser, signUpUser } from '@/api/Auth';
import { updateUserInfo } from '../function';

dayjs.extend(customParseFormat);
type OTPProps = GetProps<typeof Input.OTP>;
function SignUp() {
	const { t } = useTranslation();
	const [step, setStep] = useState(1);
	const [showPassword, setShowPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);
	const [user, setUser] = useState<any>(null);

	const [loading, setLoading] = useState(false);
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [referralCode, setReferralCode] = useState('');
	const [termsAccepted, setTermsAccepted] = useState(false);


	const [fullName, setFullName] = useState('');
	const [dob, setDob] = useState('');

	const [errorMessage, setErrorMessage] = useState('');

	async function verifyOtp(otp: string, uid: string | null) {
		try {
			if (!uid) {
				message.error('User ID is missing!');
				return;
			}
			if (otp.length !== 6) {
				message.error('Please enter a valid 6-digit OTP code!');
				return;
			}

			const res = await fetch(API_USER_URL + '/api/v1/be/verify-email-otp', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					user_id: uid,
					otp: otp
				}),
			});
			const data = await res.json();

			if (data.status) {
				message.success('OTP verified successfully!');
				setStep(3);
				return;
			} else {
				message.error(data.message || 'OTP verification failed, please try again!');
			}
		}
		catch (error) {
			message.error('OTP verification failed, please try again!');
		}
	}

	async function resendOtp(uid: string | null) {
		try {
			if (!uid) {
				message.error('User ID is missing!');
				return;
			}
			const res = await fetch(API_USER_URL + '/api/v1/be/resend-verification-otp', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					user_id: uid,
				}),
			});
			const data = await res.json();
			if (data.status) {
				message.success('OTP resent successfully! Please check your email.');
			} else {
				message.error(data.message || 'Failed to resend OTP, please try again!');
			}
		}
		catch (error) {
			message.error('Failed to resend OTP, please try again!');
		}
	}

	const [otpValue, setOtpValue] = useState('');
	useEffect(() => {
		if (otpValue.length === 6) {
			verifyOtp(otpValue, user ? user.id : null);
		}
	}, [otpValue]);
	const onChange: OTPProps['onChange'] = async (text) => {
		setOtpValue(text);
	};

	const onInput: OTPProps['onInput'] = (value) => {
		// change every input
	};

	const sharedProps: OTPProps = {
		onChange,
		onInput,
	};

	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		const referral_code = params.get('ref');
		if (referral_code) {
			setReferralCode(referral_code);
		}
	}, []);

	useEffect(() => {
		if (password && confirmPassword && password !== confirmPassword) {
			setErrorMessage('Passwords do not match');
		} else {
			setErrorMessage('');
		}
	}, [password, confirmPassword]);

	return (
		<div className="max-w-[460px] mx-auto bg-white p-9 rounded-2xl border-0 shadow-[0_0.5rem_1rem_rgba(2,2,76,0.05)]">
			<Helmet>
				<title>Sign Up - BeQs Reward</title>
				<meta name="description" content="Sign up to create your secure admin account on BeQs Reward." />
			</Helmet>
			{step === 1 && (
				<>
					<div className="text-center mb-4">
						{/* <img src="/images/logo/logo2.png" alt="beqs reward logo" className="mx-auto mb-4 w-24 h-10" /> */}
						<h5 className="mb-1 text-lg font-semibold">{t('auth.welcome')}</h5>
						<p className="text-sm text-text-secondary">{t('auth.signup_desc')}</p>
					</div>
					<div>
						<div className="mb-4">
							<label className="block text-sm font-medium text-text-tertiary mb-1" htmlFor="registerEmail">{t('auth.email')}</label>
							<input
								value={email} onChange={(e) => setEmail(e.target.value)}
								type="email" className="mt-1 block w-full rounded-md border border-gray-300 bg-white text-gray-900 p-3" id="registerEmail" placeholder={t('auth.email_placeholder')} />
						</div>
						<div className="mb-4">
							<label className="block text-sm font-medium text-text-tertiary mb-1" htmlFor="registerPassword">{t('auth.password')}</label>
							<div className="relative">
								<input
									value={password} onChange={(e) => setPassword(e.target.value)}
									type={
										showPassword ? 'text' : 'password'
									} className="mt-1 block w-full rounded-md border border-gray-300 bg-white text-gray-900 p-3 pr-10" id="registerPassword" placeholder="********" />
								<button type="button" id="togglePassword" className="absolute right-2 top-1/2 -translate-y-1/2 !text-gray-500 hover:!text-gray-700" aria-pressed="false" aria-label="Show password" title="Show password">
									<button onClick={() => setShowPassword(!showPassword)} className="flex items-center justify-center w-8 h-8 hover:text-gray-700">
										{showPassword ?
											(
												<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="#000000" viewBox="0 0 256 256"><path d="M247.31,124.76c-.35-.79-8.82-19.58-27.65-38.41C194.57,61.26,162.88,48,128,48S61.43,61.26,36.34,86.35C17.51,105.18,9,124,8.69,124.76a8,8,0,0,0,0,6.5c.35.79,8.82,19.57,27.65,38.4C61.43,194.74,93.12,208,128,208s66.57-13.26,91.66-38.34c18.83-18.83,27.3-37.61,27.65-38.4A8,8,0,0,0,247.31,124.76ZM128,192c-30.78,0-57.67-11.19-79.93-33.25A133.47,133.47,0,0,1,25,128,133.33,133.33,0,0,1,48.07,97.25C70.33,75.19,97.22,64,128,64s57.67,11.19,79.93,33.25A133.46,133.46,0,0,1,231.05,128C223.84,141.46,192.43,192,128,192Zm0-112a48,48,0,1,0,48,48A48.05,48.05,0,0,0,128,80Zm0,80a32,32,0,1,1,32-32A32,32,0,0,1,128,160Z"></path></svg>
											) :
											(
												<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="#000000" viewBox="0 0 256 256"><path d="M53.92,34.62A8,8,0,1,0,42.08,45.38L61.32,66.55C25,88.84,9.38,123.2,8.69,124.76a8,8,0,0,0,0,6.5c.35.79,8.82,19.57,27.65,38.4C61.43,194.74,93.12,208,128,208a127.11,127.11,0,0,0,52.07-10.83l22,24.21a8,8,0,1,0,11.84-10.76Zm47.33,75.84,41.67,45.85a32,32,0,0,1-41.67-45.85ZM128,192c-30.78,0-57.67-11.19-79.93-33.25A133.16,133.16,0,0,1,25,128c4.69-8.79,19.66-33.39,47.35-49.38l18,19.75a48,48,0,0,0,63.66,70l14.73,16.2A112,112,0,0,1,128,192Zm6-95.43a8,8,0,0,1,3-15.72,48.16,48.16,0,0,1,38.77,42.64,8,8,0,0,1-7.22,8.71,6.39,6.39,0,0,1-.75,0,8,8,0,0,1-8-7.26A32.09,32.09,0,0,0,134,96.57Zm113.28,34.69c-.42.94-10.55,23.37-33.36,43.8a8,8,0,1,1-10.67-11.92A132.77,132.77,0,0,0,231.05,128a133.15,133.15,0,0,0-23.12-30.77C185.67,75.19,158.78,64,128,64a118.37,118.37,0,0,0-19.36,1.57A8,8,0,1,1,106,49.79,134,134,0,0,1,128,48c34.88,0,66.57,13.26,91.66,38.35,18.83,18.83,27.3,37.62,27.65,38.41A8,8,0,0,1,247.31,131.26Z"></path></svg>
											)
										}
									</button>
								</button>
							</div>
						</div>
						<div className={` ${errorMessage ? 'mb-6' : 'mb-4'}`}>
							<label className="block text-sm font-medium text-text-tertiary mb-1" htmlFor="registerConfirmPassword">{t('auth.confirm_password')}</label>
							<div className="relative">
								<input
									value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
									type={
										showConfirmPassword ? 'text' : 'password'
									} className="mt-1 block w-full rounded-md border border-gray-300 bg-white text-gray-900 p-3" id="registerConfirmPassword" placeholder="********" />
								<button type="button" id="togglePassword" className="absolute right-2 top-1/2 -translate-y-1/2 !text-gray-500 hover:!text-gray-700" aria-pressed="false" aria-label="Show password" title="Show password">
									<button onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="flex items-center justify-center w-8 h-8 hover:text-gray-700">
										{showConfirmPassword ?
											(
												<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="#000000" viewBox="0 0 256 256"><path d="M247.31,124.76c-.35-.79-8.82-19.58-27.65-38.41C194.57,61.26,162.88,48,128,48S61.43,61.26,36.34,86.35C17.51,105.18,9,124,8.69,124.76a8,8,0,0,0,0,6.5c.35.79,8.82,19.57,27.65,38.4C61.43,194.74,93.12,208,128,208s66.57-13.26,91.66-38.34c18.83-18.83,27.3-37.61,27.65-38.4A8,8,0,0,0,247.31,124.76ZM128,192c-30.78,0-57.67-11.19-79.93-33.25A133.47,133.47,0,0,1,25,128,133.33,133.33,0,0,1,48.07,97.25C70.33,75.19,97.22,64,128,64s57.67,11.19,79.93,33.25A133.46,133.46,0,0,1,231.05,128C223.84,141.46,192.43,192,128,192Zm0-112a48,48,0,1,0,48,48A48.05,48.05,0,0,0,128,80Zm0,80a32,32,0,1,1,32-32A32,32,0,0,1,128,160Z"></path></svg>
											) :
											(
												<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="#000000" viewBox="0 0 256 256"><path d="M53.92,34.62A8,8,0,1,0,42.08,45.38L61.32,66.55C25,88.84,9.38,123.2,8.69,124.76a8,8,0,0,0,0,6.5c.35.79,8.82,19.57,27.65,38.4C61.43,194.74,93.12,208,128,208a127.11,127.11,0,0,0,52.07-10.83l22,24.21a8,8,0,1,0,11.84-10.76Zm47.33,75.84,41.67,45.85a32,32,0,0,1-41.67-45.85ZM128,192c-30.78,0-57.67-11.19-79.93-33.25A133.16,133.16,0,0,1,25,128c4.69-8.79,19.66-33.39,47.35-49.38l18,19.75a48,48,0,0,0,63.66,70l14.73,16.2A112,112,0,0,1,128,192Zm6-95.43a8,8,0,0,1,3-15.72,48.16,48.16,0,0,1,38.77,42.64,8,8,0,0,1-7.22,8.71,6.39,6.39,0,0,1-.75,0,8,8,0,0,1-8-7.26A32.09,32.09,0,0,0,134,96.57Zm113.28,34.69c-.42.94-10.55,23.37-33.36,43.8a8,8,0,1,1-10.67-11.92A132.77,132.77,0,0,0,231.05,128a133.15,133.15,0,0,0-23.12-30.77C185.67,75.19,158.78,64,128,64a118.37,118.37,0,0,0-19.36,1.57A8,8,0,1,1,106,49.79,134,134,0,0,1,128,48c34.88,0,66.57,13.26,91.66,38.35,18.83,18.83,27.3,37.62,27.65,38.41A8,8,0,0,1,247.31,131.26Z"></path></svg>
											)
										}
									</button>
								</button>
								{errorMessage && <p className="absolute text-red-500 text-sm mt-1 -bottom-5">{errorMessage}</p>}
							</div>
						</div>
						
						<div className="mb-4">
							<label className="block text-sm font-medium text-text-tertiary mb-1" htmlFor="referralCode">{t('auth.referral_code')}</label>
							<input
								value={referralCode} onChange={(e) => setReferralCode(e.target.value)}
								type="text" className="mt-1 block w-full rounded-md border border-gray-300 bg-white text-gray-900 p-3" id="referralCode" placeholder={t('auth.referral_placeholder')} />
						</div>
						<div className="mb-3">
							<div className="flex items-center !space-x-2">
								<input
									checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)}
									className="h-4 w-4 !text-primary !no-underline border-gray-300 !rounded" type="checkbox" id="termsConditions" name="terms" />
								<label className="text-sm text-text-tertiary" htmlFor="termsConditions">
									{t('auth.terms_agree')} <a href="" className="!text-primary !no-underline">{t('auth.terms_conditions')}</a>
								</label>
							</div>
						</div>
						<div className="mb-3">
							<button type="button"
								disabled={!termsAccepted || !email || !password || !confirmPassword || (password !== confirmPassword)}
								onClick={async () => {
									!loading && setLoading(true);
									const registeredUser = await signUpUser(email, password, confirmPassword, referralCode);
									if (registeredUser) {
										setUser(registeredUser);
										setStep(2);
									}
									setLoading(false);
								}}
								className="w-full bg-primary hover:bg-primary/90 !text-white disabled:opacity-50 disabled:!cursor-not-allowed font-semibold py-3 px-4 rounded">
								{loading ? <span className="flex items-center justify-center gap-2">
									<Spinner size={24} /> {t('auth.creating_account')}
								</span> : t('auth.sign_up')}
							</button>
						</div>
						<p className="mb-1 text-center text-sm text-text-tertiary">{t('auth.have_account')} <Link to="/login" className="ml-1 !text-primary !no-underline">{t('auth.login_here')}</Link></p>
						<div className="relative my-6 text-center">
							<div className="border-t border-gray-200"></div>
							<span className="px-3 absolute left-1/2 -translate-x-1/2 -top-3 inline-block text-sm text-text-tertiary bg-white whitespace-nowrap">
								{t('auth.or_continue_with')}
							</span>
						</div>
						<GoogleLogin />
					</div>
				</>
			)}
			{step === 2 && (
				<>
					<Flex justify="center" align="center" gap={16}>
						<Input.OTP
							{...sharedProps}
							value={otpValue}
							onChange={(value) => setOtpValue(value)}
							length={6}
							size="large"
							autoFocus
						/>
					</Flex>
					<button type="button"
						onClick={async () => {
							verifyOtp(otpValue, user ? user.id : null);
						}}
						className="w-full !mt-6 bg-primary hover:bg-primary/90 !text-white font-semibold py-2 px-4 rounded">{t('auth.verify_otp')}</button>
					<div className="mt-4 text-center">
						<p className="text-sm text-gray-600">{t('auth.didnt_receive_otp')} <button onClick={() => {
							resendOtp(user ? user.id : null);
						}} className="ml-1 !text-primary !no-underline">{t('auth.resend_otp')}</button></p>
					</div>
				</>
			)}
			{step === 3 && (
				<>
					<div className="text-center mb-4">
						<h5 className="mb-1 text-lg font-semibold">{t('auth.welcome')}</h5>
						<p className="text-sm text-gray-600">{t('auth.update_info')}</p>
					</div>
					<div className="flex flex-row mb-4">
						<div className="w-full">
							<label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="registerEmail">{t('auth.full_name')}</label>
							<input value={fullName} onChange={(e) => setFullName(e.target.value)}
								type="text" className="mt-1 block w-full rounded-md border border-gray-300 bg-white text-gray-900 p-2" id="firstName" placeholder={t('auth.full_name')} />
						</div>
					</div>
					<div className="mb-4">
						<label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="registerEmail">{t('auth.dob')}</label>
						<DatePicker
							format={["YYYY-MM-DD"]}
							placeholder={t('auth.dob_placeholder')}
							value={dob ? dayjs(dob, "YYYY-MM-DD") : null}
							onChange={(date, dateString) => setDob(Array.isArray(dateString) ? dateString[0] : (dateString || ''))}
							disabledDate={(current) => {
								return current && current > dayjs().endOf("day");
							}}
							inputReadOnly={false}
							allowClear={true}
							className="!bg-transparent border-none !text-gray-900-100 w-full h-12 ring-0 focus:ring-0 !rounded-md"
						/>
					</div>
					<div>
						<button type="button"
							disabled={!fullName || !dob || loading}
							onClick={async () => {
								!loading && setLoading(true);
								const status = await updateUserInfo(user.id, fullName, dob);
								if (status) {
									message.success('User info updated successfully!');
									await loginUser(email, password);
								}
								else {
									message.error('Failed to update user info, please try again!');
									return;
								}
								setLoading(false);
							}}
							className="w-full bg-primary hover:bg-primary/90 !text-white font-semibold py-2 px-4 rounded">{t('auth.continue')}</button>
					</div>
				</>
			)}
		</div>
	);
}

export default SignUp;